import Breadcrumb from "component/common/breadcrumb";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import strings from "translate/language";
import usePermission from "utils/usePermission";
import { handleListResponse, seo } from "utils/utils";
import { getBookingList } from "../../reduxStore/action/bookingActions";
import bookingService from "../../reduxStore/service/bookingService";
import TableDataNotFound from "component/TableDataNotFound";
import usePage from "utils/usePage";
import { formatBookingIstDateTime } from "utils/latestLeadStatusFilter";
import { bookingStatus } from "../../utils/constants";
import Pagination from "component/common/Pagination";
import { getReportPropertyList } from "reduxStore/action/reportActions";
import { getAgentProperty } from "reduxStore/action/propertyActions";
import Select from "react-select";
import { toast } from "react-toastify";
import "./BookingListing.css";

const getBookingStatusBadgeClass = (status) => {
  const value = Number(status);
  if (value === 1) return "booking-status-badge--ready";
  if (value === 2) return "booking-status-badge--booking";
  if (value === 3) return "booking-status-badge--complete";
  if (value === 4) return "booking-status-badge--cancelled";
  return "booking-status-badge--default";
};

const BookingStatusIcon = ({ tone = "success" }) => (
  <span className="booking-status-badge__icon" aria-hidden="true">
    <i
      className={
        tone === "failure" ? "fa fa-exclamation-circle" : "fa fa-check-circle"
      }
    />
  </span>
);

const getBookingStatusIconTone = (status) =>
  Number(status) === 4 ? "failure" : "success";

const BookingList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [page, setPage] = usePage();
  const [formVaild, setFormVaild] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(true);
  const [propertyList, setPropertyList] = useState([]);
  const [bookingList, setBookingList] = useState([]);
  const [syncingBookingId, setSyncingBookingId] = useState(null);
  const [syncingAllToJw, setSyncingAllToJw] = useState(false);
  const [pendingJwCount, setPendingJwCount] = useState(0);
  const ReportListSelector = useSelector((state) => state.report);
  const navigatpage = async (navname) => {
    navigate(navname);
  };
  const bookingListSelector = useSelector((state) => state?.booking);
  const propertySelector = useSelector((state) => state.propertyData);
  const { isAdmin } = usePermission({});

  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];

  const queryStart = query.get("start_date");
  const queryEnd = query.get("end_date");
  const hiddenStatus = location.state?.hiddenStatus || "";

  const todayIso = formatDate(today);
  const defaultStart = queryStart || todayIso;
  const defaultEnd = queryEnd || todayIso;

  const [form, setForm] = useState({
    start_date: defaultStart || "",
    end_date: defaultEnd || "",
    limit: "",
    offset: "",
    booking_status: hiddenStatus,
    property_name: "",
    site: null,
    customer_name: "",
  });

  const buildListParams = (offset = 0, source = form) => ({
    limit: 10,
    offset,
    start_date: source.start_date || "",
    end_date: source.end_date || "",
    booking_status: source.booking_status || "",
    property_name: source?.site?.property_title || "",
    customer_name: source.customer_name || "",
  });

  // Keep last applied filters so pagination does not fall back to defaults
  const appliedFiltersRef = useRef(buildListParams(0));


  useEffect(() => {
    dispatch(getAgentProperty({ offset: 0, limit: "" }));
  }, [dispatch]);

  useEffect(() => {
    if (isAdmin) {
      const { reportPropertyList } = ReportListSelector;
      if (reportPropertyList) {
        if (reportPropertyList?.status === 200) {
          setPropertyList(handleListResponse(reportPropertyList.data));
        } else {
          setPropertyList([]);
        }
      }
    }
  }, [ReportListSelector]);

  useEffect(() => {
    if (propertySelector.response) {
      if (
        propertySelector?.response &&
        propertySelector?.response.status === 200
      ) {
        const filteredData = propertySelector?.response?.data?.filter(
          (item) => item.status === true
        );
        if (filteredData?.length > 0) {
          setPropertyList(handleListResponse(filteredData));
        } else {
          setPropertyList([]); // Clear the list if no valid data
        }
      } else {
        setPropertyList([]);
      }
    }
  }, [dispatch, propertySelector]);

  useEffect(() => {
    const response = bookingListSelector?.response;
    if (!response) return;
    if (response.status === 200) {
      const rows = response?.data;
      setBookingList(
        Array.isArray(rows) && rows.length > 0
          ? handleListResponse(rows)
          : []
      );
      setPendingJwCount(Number(response?.jw_pending_count) || 0);
      return;
    }
    setBookingList([]);
    setPendingJwCount(0);
  }, [bookingListSelector]);

  // Fetch with applied filters whenever page changes (including first load)
  useEffect(() => {
    dispatch(
      getBookingList({
        ...appliedFiltersRef.current,
        limit: 10,
        offset: page,
      })
    );
  }, [dispatch, page]);

  const filterDetail = () => {
    if (handleValid()) return;
    const params = buildListParams(0);
    appliedFiltersRef.current = params;
    if (page !== 0) {
      setPage(0);
      return;
    }
    dispatch(getBookingList(params));
  };

  const filterReset = () => {
    const resetForm = {
      start_date: defaultStart,
      end_date: defaultEnd,
      booking_status: hiddenStatus,
      property_name: "",
      site: null,
      customer_name: "",
    };
    const params = buildListParams(0, resetForm);
    setForm(resetForm);
    setFormVaild({
      ...formVaild,
      end_date: "",
    });
    appliedFiltersRef.current = params;
    if (page !== 0) {
      setPage(0);
      return;
    }
    dispatch(getBookingList(params));
  };

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;
    if (form.start_date !== "") {
      if (form.end_date === "") {
        formError = true;
        err["end_date"] = strings.endDateErr;
      }
    }
    setFormVaild(err);
    return formError;
  };

  const handleInputField = (e) => {
    const nextFormState = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(nextFormState);
    if (e.target.name === "end_date" || e.target.name === "start_date") {
      setFormVaild({
        ...formVaild,
        end_date: "",
      });
    }
  };

  const handlePageClick = (newOffset) => {
    setPage(newOffset);
  };

  useEffect(() => {
    setEndDateDisable(form.start_date === "");
  }, [form.start_date]);

  useEffect(() => {
    seo({
      title: "Booking Listing",
      metaDescription: "Booking list",
      canonicalLink: window.location.href,
    });
  }, []);

  const { view } = usePermission({ view: "view_booking" });

  const handleSyncToJw = async (item) => {
    if (!item?._id || syncingBookingId || syncingAllToJw) return;
    setSyncingBookingId(item._id);
    try {
      const res = await bookingService.syncBookingToJW({
        booking_id: item._id,
      });
      if (res?.data?.status === 200) {
        toast.success(res?.data?.message || strings.jwSyncSuccess);
        const jwNo = res?.data?.data?.booking_jw_no;
        setBookingList((prev) =>
          (prev || []).map((row) =>
            row?._id === item._id
              ? { ...row, booking_jw_no: jwNo || row.booking_jw_no }
              : row,
          ),
        );
        setPendingJwCount((count) => Math.max(0, count - 1));
      } else {
        toast.error(
          res?.data?.message || "JW sync failed. Please try again.",
        );
      }
    } catch (e) {
      toast.error(e?.message || "JW sync failed. Please try again.");
    } finally {
      setSyncingBookingId(null);
    }
  };

  const needsJwSync = (item) =>
    Number(item?.booking_status) !== 4 &&
    (!item?.booking_jw_no || String(item.booking_jw_no).trim() === "");

  const handleSyncAllToJw = async () => {
    if (syncingAllToJw || syncingBookingId) return;
    const pending = (bookingList || []).filter(needsJwSync);
    if (!pending.length) {
      toast.info(strings.syncAllToJwNone);
      return;
    }
    setSyncingAllToJw(true);
    let synced = 0;
    let failed = 0;
    try {
      for (const item of pending) {
        try {
          const res = await bookingService.syncBookingToJW({
            booking_id: item._id,
          });
          if (res?.data?.status === 200) {
            synced += 1;
            const jwNo = res?.data?.data?.booking_jw_no;
            setBookingList((prev) =>
              (prev || []).map((row) =>
                row?._id === item._id
                  ? { ...row, booking_jw_no: jwNo || row.booking_jw_no }
                  : row,
              ),
            );
          } else {
            failed += 1;
          }
        } catch {
          failed += 1;
        }
      }
      if (synced > 0) {
        setPendingJwCount((count) => Math.max(0, count - synced));
      }
      if (failed > 0) {
        toast.error(
          `${strings.syncAllToJwDone} Synced ${synced}, failed ${failed}.`,
        );
      } else {
        toast.success(`${strings.syncAllToJwDone} Synced ${synced}.`);
      }
    } finally {
      setSyncingAllToJw(false);
    }
  };

  const renderCell = (text, toneClass = "") => {
    const value = text == null || text === "" ? "-" : text;
    const tone = value === "-" ? "booking-listing__empty" : toneClass;
    return (
      <span className={`booking-listing__cell ${tone}`.trim()}>{value}</span>
    );
  };

  return (
    <div className="page-body justo-booking-page">
      <Breadcrumb
        title={strings.booking}
        titleactive={strings.bookingListing}
      />
      <div className="container-fluid justo-booking">
        <div className="justo-booking__card">
          <div className="justo-booking__top">
            <div className="justo-booking__title-block">
              <h1 className="justo-booking__title">{strings.bookingList}</h1>
              <p className="justo-booking__subtitle">
                {strings.bookingListSubtitle}
              </p>
            </div>
            <div className="justo-booking__top-actions">
              {pendingJwCount > 0 ? (
                <span
                  className="justo-booking__status-chip booking-status-badge--pending"
                  title={`${pendingJwCount} ${strings.jwSyncPending}`}
                >
                  <BookingStatusIcon tone="failure" />
                  <span className="booking-status-badge__label">
                    {pendingJwCount} {strings.jwSyncPending}
                  </span>
                </span>
              ) : null}
              <button
                type="button"
                className="justo-booking__btn justo-booking__btn--outline"
                disabled={syncingAllToJw || !!syncingBookingId}
                onClick={handleSyncAllToJw}
              >
                {syncingAllToJw ? "Syncing..." : strings.syncAllToJw}
              </button>
            </div>
          </div>

          <form className="justo-booking__filters" onSubmit={(e) => e.preventDefault()}>
            <div className="justo-booking__filters-grid">
              <label className="justo-booking__field">
                <span className="justo-booking__label">{strings.startDate}</span>
                <input
                  className="form-control justo-booking__input"
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleInputField}
                />
              </label>
              <label className="justo-booking__field">
                <span className="justo-booking__label">{strings.endDate}</span>
                <input
                  className="form-control justo-booking__input"
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleInputField}
                  min={form.start_date}
                  disabled={endDateDisable}
                />
                {formVaild.end_date ? (
                  <span className="justo-booking__error">{formVaild.end_date}</span>
                ) : null}
              </label>
              <label className="justo-booking__field">
                <span className="justo-booking__label">{strings.searchByStatus}</span>
                <select
                  className="form-control justo-booking__input"
                  name="booking_status"
                  value={form.booking_status}
                  onChange={handleInputField}
                >
                  <option value="">{strings.selectstatus}</option>
                  <option value={1}>{strings.readyToBook}</option>
                  <option value={2}>{strings.booking}</option>
                  <option value={3}>{strings.complete}</option>
                  <option value={4}>{strings.bookingCancel}</option>
                </select>
              </label>
              <div className="justo-booking__field">
                <span className="justo-booking__label">
                  {strings.searchByPropertyName}
                </span>
                <Select
                  classNamePrefix="justo-booking-select"
                  isSearchable
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  onInputChange={(e) => {
                    if (isAdmin) {
                      if (e.length > 1) {
                        dispatch(
                          getReportPropertyList({
                            search_site: e,
                            isLoadMore: true,
                          }),
                        );
                      } else {
                        dispatch(
                          getReportPropertyList({
                            limit: 10,
                            offset: 0,
                            isLoadMore: true,
                          }),
                        );
                      }
                    }
                  }}
                  placeholder={strings.searchByPropertyName}
                  options={propertyList}
                  getOptionLabel={(option) => `${option.property_title}`}
                  getOptionValue={(option) => option._id}
                  name="site"
                  value={form.site}
                  onChange={(e) => {
                    setForm({ ...form, site: e });
                  }}
                />
              </div>
              <label className="justo-booking__field">
                <span className="justo-booking__label">
                  {strings.searchByVisitorName}
                </span>
                <input
                  className="form-control justo-booking__input"
                  type="text"
                  autoComplete="off"
                  placeholder={strings.searchVisitorName}
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleInputField}
                />
              </label>
              <div className="justo-booking__filter-actions">
                <button
                  className="justo-booking__btn justo-booking__btn--solid"
                  type="button"
                  onClick={filterDetail}
                >
                  {strings.search}
                </button>
                <button
                  className="justo-booking__btn justo-booking__btn--ghost"
                  type="button"
                  onClick={filterReset}
                >
                  {strings.reset}
                </button>
              </div>
            </div>
          </form>

          <div className="justo-booking__table-wrap">
            <div className="table-responsive booking-listing--hybrid__scroll">
              <table className="table booking-listing--hybrid__table">
                <thead>
                  <tr>
                    <th scope="col" className="booking-listing__text-left">
                      {strings.propertyName}
                    </th>
                    <th scope="col" className="booking-listing__text-center">
                      {strings.bookingDate}
                    </th>
                    <th scope="col" className="booking-listing__text-left">
                      {strings.visitorName}
                    </th>
                    <th scope="col" className="booking-listing__text-center">
                      {strings.configuration}
                    </th>
                    <th scope="col" className="booking-listing__text-center">
                      {strings.budget}
                    </th>
                    <th
                      scope="col"
                      className="booking-listing__text-center booking-listing__status-col"
                    >
                      {strings.status}
                    </th>
                    <th
                      scope="col"
                      className="booking-listing__text-center booking-listing__jw-col"
                    >
                      {strings.jwBookingNo}
                    </th>
                    <th
                      scope="col"
                      className="booking-listing__text-center booking-listing__actions-col"
                    >
                      {strings.action}
                    </th>
                  </tr>
                </thead>
                      <tbody>
                        {bookingList?.length > 0 ? (
                          bookingList.map((item, index) => {
                            const isJwSynced =
                              item?.booking_jw_no &&
                              String(item.booking_jw_no).trim() !== "";
                            const showSyncBtn =
                              Number(item?.booking_status) !== 4 && !isJwSynced;
                            const mobile = item?.mobile;
                            const budgetText = `${item?.min_budget || ""}${
                              item.min_budget ? item.min_budget_type || "" : ""
                            } - ${item?.max_budget || ""}${
                              item.max_budget ? item.max_budget_type || "" : ""
                            }`.trim();
                            return (
                              <tr key={item._id || index}>
                                <td className="booking-listing__text-left">
                                  {renderCell(
                                    item.property_title,
                                    "booking-listing__primary",
                                  )}
                                </td>
                                <td className="booking-listing__text-center">
                                  {renderCell(
                                    formatBookingIstDateTime(
                                      item.booking_date,
                                      item.createdDate,
                                      { listing: true },
                                    ),
                                    "booking-listing__date",
                                  )}
                                </td>
                                <td className="booking-listing__text-left">
                                  {renderCell(
                                    `${item.customer_first_name || ""} ${
                                      item.customer_last_name || ""
                                    }`.trim(),
                                    "booking-listing__primary",
                                  )}
                                </td>
                                <td className="booking-listing__text-center">
                                  {renderCell(
                                    item.configuration,
                                    "booking-listing__body",
                                  )}
                                </td>
                                <td className="booking-listing__text-center">
                                  {renderCell(
                                    budgetText === "-" || budgetText === ""
                                      ? "-"
                                      : budgetText,
                                    "booking-listing__muted",
                                  )}
                                </td>
                                <td className="booking-listing__text-center booking-listing__status-col">
                                  {bookingStatus[item?.booking_status] ? (
                                    <div className="booking-listing__status-wrap">
                                      <span
                                        className={`booking-status-badge ${getBookingStatusBadgeClass(
                                          item?.booking_status,
                                        )}`}
                                        title={
                                          bookingStatus[item?.booking_status]
                                        }
                                      >
                                        <BookingStatusIcon
                                          tone={getBookingStatusIconTone(
                                            item?.booking_status,
                                          )}
                                        />
                                        <span className="booking-status-badge__label">
                                          {bookingStatus[item?.booking_status]}
                                        </span>
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="booking-listing__empty">
                                      -
                                    </span>
                                  )}
                                </td>
                                <td className="booking-listing__text-center booking-listing__jw-col">
                                  {Number(item?.booking_status) === 4 ? (
                                    <span className="booking-listing__empty">
                                      -
                                    </span>
                                  ) : isJwSynced ? (
                                    <div className="booking-listing__jw-status">
                                      <span
                                        className="booking-status-badge booking-status-badge--synced"
                                        title={`${strings.jwSynced}: ${item.booking_jw_no}`}
                                      >
                                        <BookingStatusIcon tone="success" />
                                        <span className="booking-status-badge__label">
                                          {strings.jwSyncedShort}
                                        </span>
                                      </span>
                                      <span className="booking-listing__jw-no">
                                        {String(item.booking_jw_no)}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="booking-listing__jw-status">
                                      <span
                                        className="booking-status-badge booking-status-badge--pending"
                                        title={strings.jwSyncPending}
                                      >
                                        <BookingStatusIcon tone="failure" />
                                        <span className="booking-status-badge__label">
                                          {strings.jwSyncPendingShort}
                                        </span>
                                      </span>
                                    </div>
                                  )}
                                </td>
                                <td className="booking-listing__text-center booking-listing__actions-col">
                                  <div className="booking-listing__action-group">
                                    <div className="booking-listing__action-icons">
                                      <span className="booking-listing__action-slot">
                                        {mobile ? (
                                          <a
                                            className="booking-listing__action-btn"
                                            href={`tel:${mobile}`}
                                            title={strings.call || "Call"}
                                          >
                                            <i className="fa fa-phone" aria-hidden="true" />
                                          </a>
                                        ) : (
                                          <span
                                            className="booking-listing__action-btn booking-listing__action-btn--placeholder"
                                            aria-hidden="true"
                                          />
                                        )}
                                      </span>
                                      <span className="booking-listing__action-slot">
                                        {view ? (
                                          <button
                                            type="button"
                                            className="booking-listing__action-btn"
                                            title="View Booking"
                                            onClick={() => {
                                              navigatpage(`/view_booking/${item._id}`);
                                            }}
                                          >
                                            <i className="fa fa-eye" aria-hidden="true" />
                                          </button>
                                        ) : (
                                          <span
                                            className="booking-listing__action-btn booking-listing__action-btn--placeholder"
                                            aria-hidden="true"
                                          />
                                        )}
                                      </span>
                                    </div>
                                    {showSyncBtn ? (
                                      <span className="booking-listing__sync-slot">
                                        <button
                                          type="button"
                                          className="justo-booking__btn justo-booking__btn--outline justo-booking__btn--sm"
                                          disabled={
                                            syncingAllToJw ||
                                            syncingBookingId === item._id
                                          }
                                          onClick={() => handleSyncToJw(item)}
                                        >
                                          {syncingBookingId === item._id
                                            ? "..."
                                            : strings.syncToJw}
                                        </button>
                                      </span>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr />
                        )}
                      </tbody>
                    </table>
                    <TableDataNotFound data={bookingList} />
                  </div>
                  <Pagination
                    totalCount={bookingListSelector?.response?.total_data}
                    listData={bookingList}
                    onPageChange={handlePageClick}
                    itemOffset={page}
                    setItemOffset={setPage}
                  />
                </div>
              </div>
            </div>
          </div>
  );
};
export default BookingList;
